import React from 'react';
import { Lightbulb, Ruler, Zap, Trophy } from 'lucide-react';

export const SuccessPathSection = () => {
  const phases = [
    {
      icon: Lightbulb,
      number: 1,
      title: 'The Visionary',
      subtitle: 'Mindset, clarity, and confidence',
      points: [
        'Overcoming fear, doubt, and burnout',
        'Self-belief and resilience',
        'Goal alignment'
      ]
    },
    {
      icon: Ruler,
      number: 2,
      title: 'The Architect',
      subtitle: 'Foundations, structure, and systems',
      points: [
        'Business and career fundamentals',
        'Financial literacy',
        'Legal and operational basics'
      ]
    },
    {
      icon: Zap,
      number: 3,
      title: 'The Hustler',
      subtitle: 'Visibility, communication, and growth',
      points: [
        'Marketing and storytelling',
        'Relationship-building',
        'Ethical visibility and audience growth'
      ]
    },
    {
      icon: Trophy,
      number: 4,
      title: 'The Trailblazer',
      subtitle: 'Scaling, sustainability, and leadership',
      points: [
        'Scaling strategies',
        'Partnerships and sponsorships',
        'Long-term sustainability and legacy'
      ]
    }
  ];

  return (
    <section id="success-path" className="py-20" style={{ backgroundColor: 'var(--pink-blush)' }}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="lhsc-heading text-3xl md:text-4xl mb-4">
            LEARNING WITHOUT HIERARCHY.
          </h2>
          <p className="lhsc-subheading text-base" style={{ color: 'var(--slate-grey)' }}>
            Enter at any stage. Engage as life evolves. No pressure. No hierarchy.
          </p>
        </div>

        {/* Timeline - Desktop Horizontal */}
        <div className="hidden lg:block mb-12">
          <div className="relative">
            {/* Connecting Line */}
            <div 
              className="absolute top-16 left-0 right-0 h-1" 
              style={{ backgroundColor: 'var(--mauve-rose)', zIndex: 0 }}
            ></div>
            
            {/* Phase Cards */}
            <div className="grid grid-cols-4 gap-6 relative z-10">
              {phases.map((phase, index) => (
                <div key={index} className="text-center">
                  {/* Icon Circle */}
                  <div 
                    className="w-32 h-32 rounded-full mx-auto mb-6 flex items-center justify-center bg-white border-4"
                    style={{ borderColor: 'var(--mauve-rose)' }}
                  >
                    <phase.icon size={48} style={{ color: 'var(--mauve-rose)' }} />
                  </div>
                  
                  {/* Content Card */}
                  <div className="bg-white p-6 border border-[var(--pink-dusty)] min-h-[280px]">
                    <div 
                      className="text-4xl lhsc-heading mb-2"
                      style={{ color: 'var(--mauve-rose)' }}
                    >
                      {phase.number}
                    </div>
                    <h3 className="lhsc-heading text-lg mb-2">{phase.title}</h3>
                    <p className="lhsc-subheading text-xs mb-4" style={{ color: 'var(--slate-grey)' }}>
                      {phase.subtitle}
                    </p>
                    <ul className="lhsc-body text-sm space-y-2 text-left">
                      {phase.points.map((point, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="mr-2" style={{ color: 'var(--mauve-rose)' }}>•</span>
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Timeline - Mobile Vertical */}
        <div className="lg:hidden space-y-8">
          {phases.map((phase, index) => (
            <div key={index} className="relative">
              {/* Connector Line */}
              {index < phases.length - 1 && (
                <div 
                  className="absolute left-16 top-32 bottom-0 w-1 -mb-8"
                  style={{ backgroundColor: 'var(--mauve-rose)' }}
                ></div>
              )}
              
              <div className="flex items-start">
                {/* Icon Circle */}
                <div 
                  className="w-32 h-32 rounded-full flex items-center justify-center bg-white border-4 flex-shrink-0 relative z-10"
                  style={{ borderColor: 'var(--mauve-rose)' }}
                >
                  <phase.icon size={48} style={{ color: 'var(--mauve-rose)' }} />
                </div>
                
                {/* Content Card */}
                <div className="bg-white p-6 border border-[var(--pink-dusty)] ml-6 flex-1">
                  <div 
                    className="text-3xl lhsc-heading mb-2"
                    style={{ color: 'var(--mauve-rose)' }}
                  >
                    {phase.number}
                  </div>
                  <h3 className="lhsc-heading text-lg mb-2">{phase.title}</h3>
                  <p className="lhsc-subheading text-xs mb-4" style={{ color: 'var(--slate-grey)' }}>
                    {phase.subtitle}
                  </p>
                  <ul className="lhsc-body text-sm space-y-2">
                    {phase.points.map((point, idx) => (
                      <li key={idx} className="flex items-start">
                        <span className="mr-2" style={{ color: 'var(--mauve-rose)' }}>•</span>
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
